import MachinesIcon from "@shared/svg/machines.svg?react";
import { MakulaIcon } from "@shared/ui/MakulaIcon";
import { MakulaImage } from "@shared/ui/MakulaImage";
import React from "react";

export type ThumbnailSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";

interface AssetThumbnailProps {
  /** Thumbnail URL from asset (already resolved with fallbacks at service layer) */
  thumbnail?: string | null;
  /** Alt text for the image */
  alt?: string;
  /** Size of the thumbnail */
  size?: ThumbnailSize;
  /** Additional className */
  className?: string;
}

// Map size tokens to Tailwind classes
const sizeClasses: Record<ThumbnailSize, { container: string; icon: string }> =
  {
    sm: { container: "w-xl h-xl", icon: "w-md h-md" },
    md: { container: "w-2xl h-2xl", icon: "w-lg h-lg" },
    lg: { container: "w-3xl h-3xl", icon: "w-xl h-xl" },
    xl: { container: "w-4xl h-4xl", icon: "w-2xl h-2xl" },
    "2xl": { container: "w-5xl h-5xl", icon: "w-3xl h-3xl" },
    "3xl": { container: "w-6xl h-6xl", icon: "w-4xl h-4xl" },
    "4xl": { container: "w-8xl h-8xl", icon: "w-5xl h-5xl" },
  };

/**
 * AssetThumbnail
 *
 * A simple, dumb thumbnail component for displaying asset images.
 * Uses MakulaImage for shimmer loading and shows machine icon as fallback.
 * Supports multiple sizes for different use cases.
 *
 * Note: Image fallback logic (asset image → workspace logo → null) is handled
 * at the service/domain layer, not here. This component just renders what it gets.
 */
export const AssetThumbnail: React.FC<AssetThumbnailProps> = ({
  thumbnail,
  alt = "Asset",
  size = "xl",
  className = "",
}) => {
  const { container } = sizeClasses[size];

  return (
    <div
      className={`flex-shrink-0 ${container} ${className} rounded-lg overflow-hidden`}
    >
      <MakulaImage
        src={thumbnail || "/images/default/thumbnail.svg"}
        alt={alt}
        fit="contain"
        rounded="rounded-lg"
      />
    </div>
  );
};
